import { Inject, Injectable, Optional } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, EventType, Router } from '@angular/router';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { TOKEN_PAGE_META_DEFAULT } from '../injection-tokens';
import { PageMeta } from '../models/page-meta';

@Injectable({
    providedIn: 'root',
})
export class PageMetaService {
    private _pageMeta$: BehaviorSubject<PageMeta> = new BehaviorSubject({});

    public pageMeta$: Observable<PageMeta> = this._pageMeta$.pipe(map((meta: PageMeta) => this.addDefaults(meta)));

    public constructor(
        private readonly titleService: Title,
        private readonly metaService: Meta,
        private readonly router: Router,
        @Inject(TOKEN_PAGE_META_DEFAULT) @Optional() private readonly pageMetaDefault: Required<PageMeta>,
    ) {
        this.subscribeForMetaChanges();
        this.subscribeForNavigationEndEvents();
        this.setPageMeta();
    }

    public setPageMeta(meta: PageMeta = {}): void {
        this._pageMeta$.next(meta);
    }

    public getPageMeta(): PageMeta {
        return this.addDefaults(this._pageMeta$.getValue());
    }

    private subscribeForMetaChanges(): void {
        this.pageMeta$.subscribe((meta) => {
            this.titleService.setTitle(meta.title);
            this.metaService.updateTag({ name: 'description', content: meta.description });
        });
    }

    private subscribeForNavigationEndEvents(): void {
        this.router.events
            .pipe(
                filter((e) => e.type === EventType.NavigationEnd),
                map(() => this.router.routerState.snapshot.root),
                map((root: ActivatedRouteSnapshot) => {
                    // traverse the route tree so child routes can
                    // inherit meta settings from parent routes
                    let child = root;
                    let meta: PageMeta = {};
                    while (child) {
                        meta = {
                            title: child.data.title ?? meta.title,
                            description: child.data.description ?? meta.description,
                        };
                        child = child.children[0];
                    }
                    return meta;
                }),
            )
            .subscribe(this._pageMeta$);
    }

    private addDefaults(meta: PageMeta): PageMeta {
        return {
            title: meta.title ?? this.pageMetaDefault.title ?? '',
            description: meta.description ?? this.pageMetaDefault.description ?? '',
        };
    }
}
